import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import { updatePluginToolbar } from "../../../common/actions";
import { getToken, getOrganizationId } from "../../../common/environment_config.es6";
import "../canvas/canvas_overlay/_canvasOverlay.scss";
import "./_avatarAnimate.scss";
/* eslint-disable react/prop-types */

/**
 * Console prefix. Every network call and socket event in this flow is logged
 * under it, so a failure on a deployed environment can be diagnosed from the
 * browser (filter the console on "AvatarAnimate") without worker log access —
 * which matters because the job widget collapses every failure into one
 * generic sentence and the DB's error column is varchar(255).
 */
const LOG = "[AvatarAnimate]";

/**
 * "Avatarları canlandır" — turns every still presenter in the course into a
 * talking clip, in one batch.
 *
 * Runs from the editor AFTER generation, deliberately: producing the course
 * leaves cheap still avatars, and the paid lipsync only happens once the author
 * likes the layout.
 *
 * Targets are enumerated from LIVE editor state, not from the generation record
 * — by the time this runs the author may have moved, deleted or added avatar
 * boxes, and only the canvas knows the truth.
 *
 * The component also DELIVERS finished clips, by two routes:
 *
 *   - on mount it reads the training's `lipsync-video` rows from
 *     idealStudioFiles and fills in any avatar box that has no clip yet. This
 *     is what rescues an author who kicked off a batch, left without saving and
 *     came back: the clip lives on its file row, not in the course state.
 *   - while open it listens on the training socket room so a clip lands the
 *     moment it is produced.
 *
 * Delivery deliberately does NOT live in the job widget. That mount is gated on
 * `globalConfig.jobWidgetVisible`, so on a course with the tray switched off the
 * clips were produced, paid for, and silently dropped.
 *
 * Both routes only ever FILL AN EMPTY SLOT — a box that already carries a
 * videoUrl is left alone, so re-opening a course never resurrects a clip the
 * author deliberately cleared.
 *
 * Note the hooks below keep running when the button renders null (React runs
 * them before the early return), which is what lets a course whose avatars are
 * all animated still receive updates.
 *
 * NOTE: the exported MP4 still shows the poster. AvatarPlayer renders the still
 * in recorder mode because a plain <video> leaves stale frames under the
 * seek-and-screenshot loop. Animating improves the editor and the learner
 * player; the export needs AvatarPlayer's own recorder renderer first.
 */
export default function AnimateAvatarsButton({ t = (k, d) => d || k, style }) {
  // The training id lives in the URL (`?id=…`), the same place
  // AvatarNarrationTab reads it from. The nav bar's `props.trainingId` is
  // undefined here, which sent the batch off with no training and no org and
  // failed every target inside the lipsync provider.
  const trainingId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("id") || undefined
      : undefined;
  // Token, org and API base all come from the shared editor config, the same
  // way AvatarNarrationTab gets them — the nav bar does not carry them as props.
  const apiUrl = process.env.MW_API_URL;

  // Live state, not the generation record: by the time this runs the author may
  // have moved, deleted or added avatar boxes, and only the canvas knows.
  const navItemsById = useSelector((s) => s.undoGroup?.present?.navItemsById ?? {});
  const pluginToolbarsById = useSelector((s) => s.undoGroup?.present?.pluginToolbarsById ?? {});
  const soundList = useSelector((s) => s.undoGroup?.present?.soundList ?? {});
  const navItemSelected = useSelector((s) => s.undoGroup?.present?.navItemSelected ?? null);

  const [status, setStatus] = useState("idle"); // idle | starting | sent | error
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]); // idealStudioFiles rows, newest first
  const dispatch = useDispatch();

  // Toolbars change on every apply, so the socket/fetch effects read the
  // current map through a ref instead of re-subscribing on each keystroke.
  const toolbarsRef = useRef(pluginToolbarsById);
  toolbarsRef.current = pluginToolbarsById;

  /**
   * Put a clip into its AvatarPlayer box. Returns false when the box is gone or
   * already animated.
   *
   * `jobStatus: "done"` is not decoration — without it AvatarPlayer stays on the
   * poster and the visor does not count the box as a video box (readiness gate,
   * scene-end detection, export manifest all read it).
   */
  const applyClip = useCallback(
    ({ navId, boxId, videoUrl }, { force = false } = {}) => {
      if (!navId || !boxId || !videoUrl) return false;
      const tb = toolbarsRef.current?.[boxId];
      if (!tb || tb.pluginId !== "AvatarPlayer") return false;
      // Automatic paths never overwrite, so re-opening a course cannot resurrect
      // a clip the author cleared. Picking a take from history is explicit, and
      // passes force.
      if (!force && tb.state?.videoUrl) return false;
      dispatch(
        updatePluginToolbar(
          boxId,
          "main",
          "state",
          ["videoUrl", "jobStatus"],
          [videoUrl, "done"],
          undefined,
          navId
        )
      );
      return true;
    },
    [dispatch]
  );

  /**
   * Every AvatarPlayer box in the course, animated or not, with the scene's
   * narration audio when there is one.
   *
   * `animated` is carried rather than filtered out: the panel's visibility keys
   * off "does this course have presenters at all", so a course whose avatars are
   * all animated still shows the control (for history and for re-runs). The
   * earlier version filtered here and vanished the moment the last clip landed.
   */
  const avatars = useMemo(() => {
    const out = [];
    Object.keys(navItemsById || {}).forEach((navId) => {
      const nav = navItemsById[navId];
      (nav?.boxes || []).forEach((boxId) => {
        const toolbar = pluginToolbarsById?.[boxId];
        if (toolbar?.pluginId !== "AvatarPlayer") return;

        const state = toolbar.state || {};
        if (!state.avatarId) return;

        // soundList entries are SoundItem: the blob url lives in `file`, not
        // `url`. Reading the wrong field found no audio for any scene, every
        // target was skipped, and the button silently hid itself.
        const sounds = soundList?.[navId];
        const first = Array.isArray(sounds) ? sounds[0] : sounds;
        const audioUrl = first?.file || first?.url;

        out.push({
          navId,
          boxId,
          avatarId: state.avatarId,
          audioUrl,
          animated: Boolean(state.videoUrl && state.jobStatus === "done"),
          sceneTitle: nav?.name || nav?.title || navId,
        });
      });
    });
    return out;
  }, [navItemsById, pluginToolbarsById, soundList]);

  /** Animatable = has narration to lipsync against and no clip yet. */
  const targets = useMemo(() => avatars.filter((a) => a.audioUrl && !a.animated), [avatars]);

  /** The same, narrowed to the scene the author is looking at. */
  const sceneTargets = useMemo(
    () => targets.filter((a) => a.navId === navItemSelected),
    [targets, navItemSelected]
  );

  // ── Delivery 1: catch up from idealStudioFiles on open ────────────────────
  // Runs once per training. The author may have closed the tab mid-batch; the
  // clips finished anyway and are sitting on their file rows.
  const caughtUpRef = useRef(false);
  useEffect(() => {
    if (!trainingId || caughtUpRef.current) return;
    // Wait until the course state is actually in the store, or every box lookup
    // misses and we would mark ourselves caught up having applied nothing.
    if (!Object.keys(pluginToolbarsById || {}).length) return;
    caughtUpRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        // limit is capped at 100 server-side (ListFilesDto @Max(100)); asking
        // for 200 made this 400 on every open, which silently killed BOTH
        // delivery-on-reopen and the take history — the catch block only
        // warned. Page through instead.
        const PAGE = 100;
        const rows = [];
        for (let offset = 0; offset < PAGE * 10; offset += PAGE) {
          const res = await fetch(
            `${apiUrl}api/ideal-studio/files?idealStudioTrainingId=${encodeURIComponent(
              trainingId
            )}&fileType=lipsync-video&limit=${PAGE}&offset=${offset}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (cancelled) return;
          if (!res.ok) {
            // Never swallow this again: a non-2xx here means no clip is ever
            // restored after a reload, and nothing else reports it.
            console.error(
              `${LOG} dosya listesi alınamadı: HTTP ${res.status}`,
              await res.text().catch(() => "")
            );
            return;
          }
          const body = await res.json();
          const page = body?.files ?? body?.data ?? body ?? [];
          if (!Array.isArray(page)) return;
          rows.push(...page);
          if (page.length < PAGE) break;
        }
        console.log(`${LOG} ${rows.length} lipsync-video kaydı okundu`);

        const mine = rows.filter((f) => f?.elementType === "AvatarPlayer" && f?.fileVirtualPath);
        // Newest first — every animate run adds a row, so this list IS the
        // take history. No separate history store to keep in sync.
        mine.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
        setHistory(mine);

        let applied = 0;
        mine.forEach((f) => {
          if (applyClip({ navId: f.genNavId, boxId: f.boxId, videoUrl: f.fileVirtualPath }))
            applied++;
        });
        if (applied) {
          console.log(`[AvatarAnimate] ${applied} clip(s) restored from file records`);
        }
      } catch (err) {
        console.warn("[AvatarAnimate] file catch-up failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trainingId, pluginToolbarsById, apiUrl, applyClip]);

  // ── Delivery 2: live, while the editor is open ────────────────────────────
  // Same room and events the job widget uses, but subscribed here so delivery
  // does not depend on the widget being mounted.
  useEffect(() => {
    if (!trainingId) return;
    const token = getToken();
    if (!token) return;

    const root = String(window.ideaLibraryRootUrl || "")
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");
    const socket = io(root, {
      transports: ["websocket", "polling"],
      auth: { token: "Bearer " + token },
    });

    socket.on("connect", () => {
      console.log(`${LOG} socket bağlandı, odaya giriliyor:`, trainingId);
      socket.emit("join_session", { trainingId });
    });
    socket.on("connect_error", (e) => console.error(`${LOG} socket bağlanamadı:`, e?.message || e));
    socket.on("disconnect", (reason) => console.warn(`${LOG} socket koptu:`, reason));

    const onCompleted = (c) => {
      const d = c?.data ?? c ?? {};
      if ((d.jobType || d.type) !== "avatar-animate") return;
      console.log(`${LOG} TAMAMLANDI ${d.navId}/${d.boxId}`, d);
      applyClip({
        navId: d.navId,
        boxId: d.boxId,
        videoUrl: d.videoUrl || d.resultUrl,
      });
    };

    // Diagnostics. The job widget renders ONE generic sentence for every
    // failure (JobModal maps job.error onto errors.generic), and the raw
    // message is varchar(255) in the DB — so the socket payload is the only
    // place the real reason is readable. Mirrored to the console so a failure
    // on a deployed environment can be diagnosed with F12 instead of worker
    // log access.
    const onUpdate = (u) => {
      const d = u?.data ?? u ?? {};
      if ((d.jobType || d.type) !== "avatar-animate") return;
      const where = d.navId ? `${d.navId}/${d.boxId || "-"}` : "batch";

      // Targets rejected during planning never become job rows, so they never
      // get a widget card — they would otherwise vanish without a trace.
      if (Array.isArray(d.rejected) && d.rejected.length) {
        console.error(`${LOG} ${d.rejected.length} hedef hiç kuyruğa alınmadı:`, d.rejected);
      }
      if (Array.isArray(d.queued) && d.queued.length) {
        console.log(`${LOG} ${d.queued.length} hedef kuyruğa alındı:`, d.queued);
      }

      if (d.status === "failed") {
        console.error(`${LOG} BAŞARISIZ ${where} — ${d.error || "(sunucu sebep göndermedi)"}`, {
          step: d.step,
          avatarName: d.avatarName,
          avatarVideoUrl: d.avatarVideoUrl,
          audioUrl: d.audioUrl,
          errorStack: d.errorStack,
          raw: d,
        });
        setError(`${d.avatarName || where}: ${d.error || "bilinmeyen hata"}`);
        return;
      }
      console.log(`${LOG} ${d.step || d.status} ${where}`, d);
    };

    socket.on("session_job_completed", onCompleted);
    socket.on("session_job_update", onUpdate);

    return () => {
      socket.off("session_job_completed", onCompleted);
      socket.off("session_job_update", onUpdate);
      socket.emit("leave_session", { trainingId });
      socket.disconnect();
    };
  }, [trainingId, applyClip]);

  /**
   * Dispatch a batch. `which` is just a subset of the same target list — the
   * scene-only button is the batch flow with one entry, NOT a second code path,
   * so nothing about the backend contract changes.
   */
  const start = async (which) => {
    const payloadTargets = (which || []).map(({ navId, boxId, avatarId, audioUrl }) => ({
      navId,
      boxId,
      avatarId,
      audioUrl,
    }));
    if (!payloadTargets.length || status === "starting") return;

    setStatus("starting");
    setError(null);
    setOpen(false);

    const body = {
      trainingId,
      idealStudioTrainingId: trainingId,
      organizationId: getOrganizationId(),
      targets: payloadTargets,
    };
    // What the server receives, verbatim. A target whose audioUrl is empty or
    // points at another environment is the single most likely cause of an
    // instant failure, and this is the only place it is visible client-side.
    console.log(`${LOG} istek gönderiliyor (${payloadTargets.length} hedef)`, body);

    try {
      const res = await fetch(`${apiUrl}api/avatar-rich-visual/animate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        // 429 is the inflight cap: the batch is already running somewhere.
        const errBody = await res.json().catch(() => ({}));
        console.error(`${LOG} istek reddedildi: HTTP ${res.status}`, errBody);
        throw new Error(errBody?.message || `HTTP ${res.status}`);
      }

      const accepted = await res.json();
      console.log(`${LOG} kuyruğa alındı`, accepted);
      // Handed off. The widget owns it from here — one card per avatar.
      setStatus("sent");
    } catch (err) {
      console.error(`${LOG} gönderim hatası`, err);
      setStatus("error");
      setError(err?.message || String(err));
    }
  };

  // Presence of presenters — NOT of pending work — decides whether the control
  // exists. A course with every avatar already animated still needs it, for the
  // take history and for re-runs.
  if (!avatars.length) return null;

  const busy = status === "starting";
  const label = busy
    ? t("avatarAnimate.starting", "Gönderiliyor…")
    : status === "sent"
      ? t("avatarAnimate.sent", "Kuyruğa alındı")
      : t("avatarAnimate.trigger", "Avatarları canlandır");

  // History is per box; the panel lists takes for the scene in view, so the
  // author compares like with like instead of scrolling the whole course.
  const sceneHistory = history.filter((f) => f.genNavId === navItemSelected);

  return (
    // canvasOverlay gives it the zoom widget's chrome (white pill, fade-in on
    // hover, z-index above the canvas); the modifier only adds our own bits.
    <div className="canvasOverlay avatar-animate" style={style}>
      {open && (
        <div className="avatar-animate__panel" role="menu">
          <button
            type="button"
            className="avatar-animate__item"
            onClick={() => start(sceneTargets)}
            disabled={busy || !sceneTargets.length}
            title={
              !sceneTargets.length
                ? t("avatarAnimate.sceneNone", "Bu sahnede canlandırılacak avatar yok")
                : undefined
            }
          >
            {t("avatarAnimate.scene", "Bu sahnedeki avatarı oynat")}
          </button>
          <button
            type="button"
            className="avatar-animate__item"
            onClick={() => start(targets)}
            disabled={busy || !targets.length}
            title={
              !targets.length
                ? t("avatarAnimate.none", "Canlandırılacak avatar kalmadı")
                : undefined
            }
          >
            {t("avatarAnimate.all", "Tüm sahnelerdeki avatarları oynat")}
            {targets.length ? ` (${targets.length})` : ""}
          </button>

          <div className="avatar-animate__sep" />
          <div className="avatar-animate__title">{t("avatarAnimate.history", "Geçmiş")}</div>
          {sceneHistory.length ? (
            <ul className="avatar-animate__history">
              {sceneHistory.map((f) => (
                <li key={f.id}>
                  <span className="avatar-animate__when">
                    {f.createdAt ? new Date(f.createdAt).toLocaleString() : "—"}
                  </span>
                  <button
                    type="button"
                    className="avatar-animate__use"
                    onClick={() => {
                      // force: picking a take is explicit, so it replaces the
                      // clip currently on the box.
                      applyClip(
                        { navId: f.genNavId, boxId: f.boxId, videoUrl: f.fileVirtualPath },
                        { force: true }
                      );
                      setOpen(false);
                    }}
                  >
                    {t("avatarAnimate.use", "Uygula")}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="avatar-animate__empty">
              {t("avatarAnimate.historyEmpty", "Bu sahne için geçmiş yok")}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="avatar-animate__btn"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
      >
        {label}
      </button>
      {error ? <div className="avatar-animate__error">{error}</div> : null}
    </div>
  );
}
